import { useTranslation } from 'react-i18next'
import { PaqueteCard } from './PaqueteCard'
import { SelectInputShippingMethods, CarrierName } from '../../../../../components'
import { isJunkBox } from '../../packingSessionDisplay'

// Right-column panel for the multi-box (≥2 paquetes) flow. Replaces
// ShippingPackageDataSection + RecommendedPackageSection with a single stack
// of self-contained Paquete cards. Each card carries its own recommended-
// options strip and PackageSelect combobox (identical visuals to the legacy
// UI) so the operator picks the empaque per box without leaving the card.
export const MultiBoxPackingPanel = ({
  boxes = [],
  activeBoxIdx = 0,
  shippingPackagesByBoxIdx = [],
  productNameBySku = {},
  productFragileBySku = {},
  isFragileOrder = false,

  // Paquetería (shipping method) — uses the same SelectInputShippingMethods
  // combobox as the legacy UI so the operator gets identical UX.
  availableShippingMethods = [],
  selectedShippingMethod = null,
  onSelectShippingMethod = () => {},
  isShippingMethodEditable = true,
  // Carrier capability — drives the bulk-label warning + per-card print button.
  // Comes from `selectedShippingMethod.carrier_supports_per_box_label` (combined flag of
  // enable_multipackage + carrier per-package support). Surfaced as a prop so
  // the parent can override during testing/staging.
  supportsPerBoxLabel = null,

  // Empaque options data — same props the legacy UI uses
  storePackingOptions = null,
  availableShippingPackages,
  isPackagelessOrder = false,
  hasPackagelessProduct = false,
  orderStoreId = null,
  focusPackageComboboxSignal = 0,

  // Handlers
  onSelectEmpaque = (boxIdx, pkg) => {},
  onCloseBox = boxIdx => {},
  onDeleteCaja = boxIdx => {},
  onAddNewBox = () => {},
  onRemoveOne = (boxIdx, sku) => {},
  onAddBulk = (boxIdx, sku, count) => {},
  canBulkAdd = false,
  onRemoveBulk = (boxIdx, sku, count) => {},
  onReopenBox = boxIdx => {},
  onPrintBoxPackingList = (boxIdx, openModal) => {},
  onPrintShippingLabel = boxIdx => {},
  onPrintBoxInvoice = boxIdx => {},
  // Click on a card body to make it the active caja so scans flow into it.
  // Parent should only act for open backend boxes (closed / virtual skipped).
  onSelectActiveBox = boxIdx => {},
  // The cards' own confirm dialogs (reopen / delete) report upward through
  // these so the parent's scan-focus rule can see them (see PaqueteCard).
  onCardDialogOpenChange = null,
  onRequestScanFocus = () => {},

  canAddNewBox = true,

  // BUGS-7317: order has no scannable items (inserts-only removal). Lets each
  // card close its box without a scan so the order can be finalized.
  allowCloseWithoutItems = false,

  // CUBBO-2440: display-only mode for terminal orders (shipped/cancelled).
  // Keeps the cajas + their documents visible but drops every mutation —
  // the paquetería lock, the "+ Añadir otra caja" CTA, and (via the cards)
  // reopen/close/delete/unscan.
  readOnly = false,
}) => {
  const { i18n } = useTranslation()
  const tr = (k, opts) => i18n.t(`generate_shipment.multi_box.${k}`, opts)
  // Read-only forces the locked paquetería display regardless of the caller's
  // editability flag.
  const shippingMethodEditable = isShippingMethodEditable && !readOnly
  // Hide junk cajas (closed + empty is operator clean-up — we don't want them
  // to reappear as "Cerrada" cards after a delete). NOT on an order with
  // nothing scannable: there every sealed caja is empty, so see isJunkBox.
  //
  // CRITICAL (BUGS-7438 follow-up — GIANI DAFIRENZE "no deja cerrar la caja"):
  // each entry's `idx` MUST stay aligned with the parent's `sessionBoxes`
  // array (and the Redux package slots). The handlers below call e.g.
  // `onCloseBox(idx)` / `onSelectActiveBox(idx)` and the parent resolves them
  // as `sessionBoxes[idx]`. Filtering the discarded boxes into a SMALLER array
  // and then re-indexing it made the first visible card resolve to
  // `sessionBoxes[0]` — which, when a hidden closed+empty box sits before the
  // open one, is a DIFFERENT, already-closed box. The close/delete/reopen/
  // activate pre-checks then hit `status === 'closed'` and silently no-op, so
  // the operator's "Cerrar caja" click never even fired the backend call.
  // Fix: keep every entry's ORIGINAL index into `boxes` and merely SKIP
  // rendering the discarded ones (leaving a hole in the index sequence). A
  // separate running counter drives the human-facing "PAQUETE N" number.
  // Shared with the parent so the cards, the parent's handler indices and the
  // "PAQUETE N" numbering all skip the exact same set. `allowCloseWithoutItems`
  // IS the "order has nothing scannable" signal, and it doubles as the escape
  // hatch here: on such an order a sealed caja is legitimately empty, so it
  // must stay visible and keep counting as cerrada.
  const isDiscarded = b => isJunkBox(b, allowCloseWithoutItems)

  // Render at minimum 1 card so the operator sees PAQUETE 1 in state A.
  // Extra placeholders appear when packages_count > boxes.length (slots
  // waiting for empaque — these become real boxes once the operator picks one).
  const slotCount = Math.max(boxes.length, shippingPackagesByBoxIdx.length, 1)
  const renderEntries = []
  let visiblePosition = 0
  for (let idx = 0; idx < slotCount; idx++) {
    const box = boxes[idx] || null
    if (isDiscarded(box)) continue
    renderEntries.push({ idx, box, virtual: !box, displayNumber: visiblePosition + 1 })
    visiblePosition++
  }
  const visibleCount = renderEntries.length
  const closedCount = renderEntries.filter(e => e.box && e.box.is_closed).length

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4">
      <header className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-0">{tr('panel_title')}</h2>
        <p className="text-sm text-gray-500 mb-0">
          {closedCount === 1 ? tr('panel_subtitle_one', { count: closedCount }) : tr('panel_subtitle_other', { count: closedCount })}
        </p>
      </header>

      {supportsPerBoxLabel === false && (
        <div className="mb-3 px-3 py-2 rounded bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
          {i18n.t('generate_shipment.multi_box.bulk_labels_notice')}
        </div>
      )}

      <div className={`mb-4 ${shippingMethodEditable ? 'flex items-center gap-3' : ''}`}>
        {shippingMethodEditable ? (
          <>
            <label className="text-sm text-gray-700 shrink-0">{tr('shipping_method_label')}</label>
            <div className="flex-1">
              <SelectInputShippingMethods
                options={availableShippingMethods}
                selectedInitial={selectedShippingMethod || 0}
                forceSelected={selectedShippingMethod || 0}
                className="w-full"
                label=""
                onChange={newValue => onSelectShippingMethod(newValue)}
              />
            </div>
          </>
        ) : (
          // Locked paquetería display — surfaces the carrier prominently
          // because the operator can no longer change it after the first
          // close. Important reference info: it determines the labels.
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">{tr('shipping_method_label')}</div>
            <div className="text-lg font-semibold text-gray-900">
              {selectedShippingMethod ? (
                // Shipment-generation surface → show the REAL carrier
                // (Uber managed as its own carrier, not the Cubbo
                // whitelabel). See CLAUDE.md / CUBBO-2185.
                <CarrierName
                  preferInternal
                  carrierName={selectedShippingMethod.carrier_name}
                  internalCarrierName={selectedShippingMethod.internal_carrier_name}
                  suffix={`${selectedShippingMethod.shipping_name} - ${selectedShippingMethod.country}`}
                />
              ) : (
                '—'
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reorder so open / virtual placeholder cajas + the "+ Añadir
                otra caja" CTA stay near the top — the operator shouldn't have
                to scroll past N closed cards every time to see the caja they
                are scanning into. Closed cards go BELOW, in the order they
                were closed. */}
      <div className="space-y-3">
        {(() => {
          const isOpenEntry = ({ box, virtual }) => virtual || !box.is_closed
          const openEntries = renderEntries.filter(isOpenEntry)
          const closedEntries = renderEntries.filter(e => !isOpenEntry(e))
          // Autofocus target: the first open caja that still NEEDS its
          // empaque — i.e. the "+ Añadir otra caja" placeholder even when
          // an open caja with empaque sits above it. On cajas that already
          // have their empaque the mount-autofocus is inert (PackageSelect
          // gates it on !hasEmpaque) and the scan input owns the focus, so
          // targeting them left the focus on the just-clicked button and
          // the operator could not scan the empaque barcode right away.
          // Falls back to the first open entry for the parent's nudge
          // signal (pick-empaque alert / quality-check complete).
          const firstNeedsEmpaqueIdx = openEntries.find(e => !shippingPackagesByBoxIdx[e.idx])?.idx
          const firstOpenIdx = firstNeedsEmpaqueIdx ?? openEntries[0]?.idx
          const renderCard = ({ box, idx, virtual, displayNumber }) => (
            <PaqueteCard
              key={virtual ? `virtual-${idx}` : (box.box_number ?? box.id ?? idx)}
              boxNumber={displayNumber}
              isActive={virtual || (idx === activeBoxIdx && !box.is_closed)}
              box={box}
              empaque={shippingPackagesByBoxIdx[idx] || null}
              productNameBySku={productNameBySku}
              productFragileBySku={productFragileBySku}
              isFragileOrder={isFragileOrder}
              storePackingOptions={storePackingOptions}
              availableShippingPackages={availableShippingPackages}
              isPackagelessOrder={isPackagelessOrder}
              hasPackagelessProduct={hasPackagelessProduct}
              orderStoreId={orderStoreId}
              shouldAutofocusCombobox={idx === firstOpenIdx}
              autofocusComboboxSignal={idx === firstOpenIdx ? focusPackageComboboxSignal : 0}
              onSelectEmpaque={pkg => onSelectEmpaque(idx, pkg)}
              onCloseBox={() => onCloseBox(idx)}
              onDeleteCaja={() => onDeleteCaja(idx)}
              onRemoveOne={sku => onRemoveOne(idx, sku)}
              onAddBulk={(sku, count) => onAddBulk(idx, sku, count)}
              canBulkAdd={canBulkAdd}
              onRemoveBulk={(sku, count) => onRemoveBulk(idx, sku, count)}
              onPrintBoxPackingList={() => onPrintBoxPackingList(idx)}
              onPrintShippingLabel={() => onPrintShippingLabel(idx)}
              onPrintBoxInvoice={() => onPrintBoxInvoice(idx)}
              onMakeActive={() => onSelectActiveBox(idx)}
              onReopenBox={() => onReopenBox(idx)}
              onCardDialogOpenChange={onCardDialogOpenChange}
              onRequestScanFocus={onRequestScanFocus}
              isLastVisibleCaja={visibleCount <= 1}
              carrierSupportsPerBoxLabel={supportsPerBoxLabel === true}
              allowCloseWithoutItems={allowCloseWithoutItems}
              readOnly={readOnly}
            />
          )
          return (
            <>
              {openEntries.map(renderCard)}
              {canAddNewBox && !readOnly && (
                <button
                  type="button"
                  onClick={onAddNewBox}
                  title={tr('add_box_title')}
                  className="w-full py-4 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                >
                  {tr('add_box')}
                </button>
              )}
              {closedEntries.map(renderCard)}
            </>
          )
        })()}
      </div>
    </section>
  )
}

export default MultiBoxPackingPanel
